import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "../components/MarketingLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, ArrowUpRight, Sparkles, Users, Mail } from "lucide-react";

const FEATURES = [
    "Acesso completo à plataforma IHTP",
    "Metodologia IHTP Happiness Development Plan®",
    "Projetos ilimitados",
    "Registo estruturado das 5 fases",
    "Upload de logotipo da empresa",
    "Indicadores e distribuição por fase",
    "Histórico completo de subscrições",
    "Gestão de utilizadores e papéis",
];

const DURATION_OPTS = [
    { key: "1m", label: "1 Mês", suffix: "" },
    { key: "6m", label: "6 Meses", suffix: "/ 6 meses", discount: 16 },
    { key: "12m", label: "12 Meses", suffix: "/ ano", discount: 25 },
];

export default function PricingPage() {
    const [plans, setPlans] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [duration, setDuration] = useState("1m");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/subscriptions/plans")
            .then(({ data }) => { setPlans(data.plans); setTiers(data.tiers); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const planFor = (tierId, dur) => plans.find((p) => p.tier === tierId && p.id.endsWith(`_${dur}`));
    const monthlyEquiv = (plan) => {
        if (!plan) return null;
        const m = plan.amount / Math.round(plan.duration_days / 30);
        return Math.round(m);
    };

    return (
        <MarketingLayout>
            <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-12" data-testid="pricing-hero">
                <div className="label-caps">Planos e subscrição</div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#0d1628] mt-6 max-w-4xl leading-[1.05]">
                    Escolha o plano da sua <span className="italic">transformação</span>.
                </h1>
                <p className="text-[#52525b] text-lg mt-8 max-w-3xl leading-relaxed">
                    Registe a sua empresa gratuitamente. Os preços variam pela dimensão da organização (nº de utilizadores) e pelo período de subscrição. Quanto maior o compromisso, maior a poupança.
                </p>
            </section>

            <section className="border-t border-[#e4e4e7]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
                    {/* Duration toggle */}
                    <div className="flex items-center gap-2 mb-12 flex-wrap" data-testid="duration-toggle">
                        <span className="label-caps mr-3">Período:</span>
                        {DURATION_OPTS.map((d) => (
                            <button
                                key={d.key}
                                onClick={() => setDuration(d.key)}
                                className={`px-5 py-2.5 text-sm border transition-colors ${duration === d.key ? "bg-[#0d1628] text-white border-[#0d1628]" : "bg-white text-[#0d1628] border-[#e4e4e7] hover:border-[#0d1628]"}`}
                                data-testid={`duration-${d.key}`}
                            >
                                {d.label}
                                {d.discount && <span className={`ml-2 text-xs ${duration === d.key ? "text-[#b89b6f]" : "text-[#b89b6f]"}`}>−{d.discount}%</span>}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="label-caps">A carregar planos...</div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-tiers-grid">
                            {tiers.map((t) => {
                                const plan = !t.contact_only ? planFor(t.id, duration) : null;
                                const isFeatured = t.id === "business";
                                const monthly = plan ? monthlyEquiv(plan) : null;
                                return (
                                    <article
                                        key={t.id}
                                        className={`card-flat flex flex-col relative ${isFeatured ? "border-[#0d1628]" : ""}`}
                                        data-testid={`tier-${t.id}`}
                                    >
                                        {isFeatured && (
                                            <div className="absolute -top-3 left-8 bg-[#0d1628] text-white text-xs px-3 py-1 tracking-wider uppercase flex items-center gap-2">
                                                <Sparkles size={12} /> Mais escolhido
                                            </div>
                                        )}
                                        <div className="label-caps text-[#b89b6f]">{t.for_companies}</div>
                                        <h2 className="font-display text-3xl text-[#0d1628] mt-3">{t.name}</h2>
                                        <div className="flex items-center gap-2 mt-4 text-sm text-[#52525b]">
                                            <Users size={14} className="text-[#b89b6f]" />
                                            {t.max_users ? <span>até <strong className="text-[#0d1628]">{t.max_users}</strong> utilizadores</span> : <span>Sem limite definido</span>}
                                        </div>

                                        {t.contact_only ? (
                                            <>
                                                <div className="font-display text-3xl text-[#0d1628] mt-6">Sob consulta</div>
                                                <div className="text-xs text-[#52525b] mt-2">Inclui SSO, multi-empresa, account manager dedicado e sessões de coaching IHTP.</div>
                                            </>
                                        ) : plan ? (
                                            <>
                                                <div className="flex items-baseline gap-2 mt-6">
                                                    <span className="font-display text-5xl text-[#0d1628]">€{plan.amount.toFixed(0)}</span>
                                                    <span className="text-sm text-[#52525b]">{DURATION_OPTS.find((d) => d.key === duration)?.suffix}</span>
                                                </div>
                                                {duration !== "1m" && monthly && (
                                                    <div className="text-xs text-[#52525b] mt-2">≈ €{monthly}/mês</div>
                                                )}
                                                {duration === "1m" && (
                                                    <div className="text-xs text-[#52525b] mt-2">Cobrado mensalmente</div>
                                                )}
                                            </>
                                        ) : null}

                                        <ul className="mt-8 space-y-3 text-sm text-[#0d1628] flex-1">
                                            {FEATURES.slice(0, 6).map((f) => (
                                                <li key={f} className="flex items-start gap-2">
                                                    <Check size={16} className="mt-0.5 shrink-0 text-[#b89b6f]" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                            {t.id !== "starter" && (
                                                <li className="flex items-start gap-2">
                                                    <Check size={16} className="mt-0.5 shrink-0 text-[#b89b6f]" />
                                                    <span>Suporte prioritário</span>
                                                </li>
                                            )}
                                            {(t.id === "enterprise" || t.id === "corporate") && (
                                                <li className="flex items-start gap-2">
                                                    <Check size={16} className="mt-0.5 shrink-0 text-[#b89b6f]" />
                                                    <span>Onboarding dedicado IHTP</span>
                                                </li>
                                            )}
                                            {t.id === "corporate" && (
                                                <li className="flex items-start gap-2">
                                                    <Check size={16} className="mt-0.5 shrink-0 text-[#b89b6f]" />
                                                    <span>SSO, multi-empresa, account manager</span>
                                                </li>
                                            )}
                                        </ul>

                                        {t.contact_only ? (
                                            <a
                                                href="https://ihavethepower.net/contactos/"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn-outline mt-8"
                                                data-testid={`cta-${t.id}`}
                                            >
                                                <Mail size={14} /> Contactar IHTP
                                            </a>
                                        ) : (
                                            <Link
                                                to="/register"
                                                className={`mt-8 ${isFeatured ? "btn-primary" : "btn-outline"}`}
                                                data-testid={`cta-${t.id}`}
                                            >
                                                Começar agora <ArrowUpRight size={16} />
                                            </Link>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* COMPARISON STRIP */}
            <section className="border-t border-[#e4e4e7] bg-[#f9f9fa]" data-testid="pricing-comparison">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-16">
                    <div className="label-caps mb-6">Que plano é o seu?</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
                        <div>
                            <div className="font-display text-2xl text-[#0d1628]">Starter</div>
                            <p className="text-[#52525b] mt-2 leading-relaxed">Pequenas equipas que querem testar a metodologia IHTP num único projeto piloto.</p>
                        </div>
                        <div>
                            <div className="font-display text-2xl text-[#0d1628]">Business</div>
                            <p className="text-[#52525b] mt-2 leading-relaxed">PME consolidadas que querem implementar a felicidade como prioridade estratégica.</p>
                        </div>
                        <div>
                            <div className="font-display text-2xl text-[#0d1628]">Enterprise</div>
                            <p className="text-[#52525b] mt-2 leading-relaxed">Grandes empresas com vários departamentos e necessidade de onboarding estruturado.</p>
                        </div>
                        <div>
                            <div className="font-display text-2xl text-[#0d1628]">Corporate</div>
                            <p className="text-[#52525b] mt-2 leading-relaxed">Grupos, multinacionais e setor público com requisitos avançados de segurança e governança.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-t border-[#e4e4e7] bg-[#f9f9fa]" data-testid="pricing-faq">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-20">
                    <div className="label-caps">Perguntas frequentes</div>
                    <h2 className="font-display text-4xl text-[#0d1628] mt-4">Dúvidas?</h2>
                    <div className="mt-10 space-y-8">
                        <div>
                            <h3 className="font-display text-2xl text-[#0d1628]">Posso registar a empresa sem pagar?</h3>
                            <p className="text-[#52525b] mt-2 leading-relaxed">Sim. O registo é gratuito. A subscrição só é necessária quando quiser começar a criar projetos e utilizar a plataforma.</p>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl text-[#0d1628]">O que conta como &quot;utilizador&quot;?</h3>
                            <p className="text-[#52525b] mt-2 leading-relaxed">Conta como utilizador qualquer pessoa com acesso à plataforma — gestores, gestores de projeto e colaboradores. O Super Admin da IHTP não conta para o limite.</p>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl text-[#0d1628]">Como efetuo o pagamento?</h3>
                            <p className="text-[#52525b] mt-2 leading-relaxed">O pagamento é feito por <strong>transferência bancária</strong> para o IBAN da Solfut. Após a encomenda recebe um email com o IBAN, montante e referência; assim que confirmarmos a receção (até 1 dia útil), a sua subscrição é ativada e a fatura é emitida.</p>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl text-[#0d1628]">Posso trocar de plano?</h3>
                            <p className="text-[#52525b] mt-2 leading-relaxed">Sim. Em qualquer momento pode adquirir um plano superior. O novo período começa imediatamente após a ativação.</p>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl text-[#0d1628]">E se ultrapassar o limite de utilizadores?</h3>
                            <p className="text-[#52525b] mt-2 leading-relaxed">A plataforma impede a criação de utilizadores acima do limite do plano. Para crescer basta passar para o plano superior.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#0d1628] text-white" data-testid="pricing-cta-bottom">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 text-center">
                    <h2 className="font-display text-4xl md:text-5xl leading-tight">Pronto para começar?</h2>
                    <p className="text-white/80 mt-6 max-w-xl mx-auto">Registe a sua empresa em minutos e escolha o plano quando estiver pronto.</p>
                    <Link
                        to="/register"
                        className="btn-primary mt-10 inline-flex"
                        style={{ background: "#fff", color: "#0d1628", borderColor: "#fff" }}
                        data-testid="pricing-bottom-register"
                    >
                        Registar a minha empresa <ArrowUpRight size={16} />
                    </Link>
                </div>
            </section>
        </MarketingLayout>
    );
}
